/**
 * Vista imprimible del cierre de caja. El usuario abre esta ruta y hace
 * Ctrl+P → "Guardar como PDF" (o impresora física). Print CSS oculta el
 * shell de navegación y deja el reporte como un documento limpio.
 *
 * Layout estilo recibo A4: header con tenant + sesión, resumen de
 * efectivo, ventas por método, movimientos, tabla de pedidos.
 */

import { useEffect } from 'react';

import { loadSession } from '../auth/session.js';
import { movementTypeLabels } from '../lib/cashReport.js';
import { formatCOP } from '../lib/format.js';
import { orderOriginLabels, orderStatusLabels } from '../lib/orders.js';
import { paymentMethodLabels } from '../lib/payments.js';
import { trpc } from '../lib/trpc.js';

const fmtDateTime = (ms: number | null): string =>
  ms == null
    ? '—'
    : new Date(ms).toLocaleString('es-CO', {
        dateStyle: 'full',
        timeStyle: 'short',
      });

export const CashReportPage = ({ sessionId }: { sessionId: string }) => {
  const detail = trpc.cash.sessionDetail.useQuery({ sessionId });
  const tenant = trpc.tenant.current.useQuery();
  const posSession = loadSession();

  // Dispara `window.print()` una vez que los datos están listos, así el
  // diálogo de impresión sale automático al abrir la ruta en una pestaña.
  useEffect(() => {
    if (!detail.data) return;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [detail.data]);

  if (detail.isLoading) {
    return (
      <div className="p-8 text-fg-muted print:hidden">Cargando reporte…</div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div className="p-8 text-danger print:hidden">
        {detail.error?.message ?? 'No se encontró la sesión'}
      </div>
    );
  }

  const d = detail.data;
  const diff =
    d.session.closingCashCents != null
      ? d.session.closingCashCents - d.session.expectedCashCents
      : null;

  return (
    <div className="mx-auto max-w-3xl p-8 text-sm text-fg print:p-0">
      {/* Encabezado */}
      <div className="mb-6 border-b-2 border-fg pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight">
              Cierre de caja
            </h1>
            <div className="mt-1 text-sm text-fg-muted">
              {tenant.data?.tenant.name ?? posSession?.tenantName ?? ''}
            </div>
          </div>
          <div className="text-right text-xs text-fg-muted">
            Generado{' '}
            {new Date().toLocaleString('es-CO', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <KV label="Cajero">
            {d.session.userFullName ??
              d.session.userEmail ??
              d.session.userId}
          </KV>
          <KV label="Sesión">
            <span className="font-mono">
              {d.session.id.slice(-12).toUpperCase()}
            </span>
          </KV>
          <KV label="Abierto">{fmtDateTime(d.session.openedAt)}</KV>
          <KV label="Cerrado">{fmtDateTime(d.session.closedAt)}</KV>
          {d.session.note ? (
            <KV label="Nota" span={2}>
              {d.session.note}
            </KV>
          ) : null}
        </div>
      </div>

      {/* Resumen de efectivo */}
      <Section title="Resumen de efectivo">
        <MoneyRow label="Apertura" cents={d.session.openingCashCents} />
        <MoneyRow label="+ Ventas en efectivo" cents={d.cashFlow.cashSalesCents} />
        {(d.cashFlow.cashCollectionsCents ?? 0) > 0 ? (
          <MoneyRow
            label="+ Abonos a cartera en efectivo"
            cents={d.cashFlow.cashCollectionsCents ?? 0}
          />
        ) : null}
        <MoneyRow label="+ Entradas manuales" cents={d.cashFlow.cashInCents} />
        <MoneyRow
          label="− Salidas manuales"
          cents={d.cashFlow.cashOutCents}
          negative
        />
        <MoneyRow
          label="− Drops a bóveda"
          cents={d.cashFlow.dropsCents}
          negative
        />
        <MoneyRow
          label="Efectivo esperado"
          cents={d.session.expectedCashCents}
          strong
          divider
        />
        {d.session.closingCashCents != null ? (
          <>
            <MoneyRow
              label="Efectivo contado"
              cents={d.session.closingCashCents}
            />
            <MoneyRow
              label="Diferencia"
              cents={diff ?? 0}
              strong
              tone={diff === 0 ? 'ok' : diff && diff < 0 ? 'bad' : 'warn'}
            />
          </>
        ) : (
          <div className="mt-2 text-xs italic text-fg-muted">
            (Turno abierto — el efectivo contado se registra al cerrar.)
          </div>
        )}
      </Section>

      {/* Ventas por método */}
      <Section title="Ventas por método de pago">
        {Object.entries(d.salesByMethod).map(([method, amount]) => (
          <MoneyRow
            key={method}
            label={paymentMethodLabels[method] ?? method}
            cents={amount}
          />
        ))}
        <MoneyRow
          label="Total ventas"
          cents={d.totalSalesCents}
          strong
          divider
        />
        {(d.creditCents ?? 0) > 0 || (d.collectionsTotalCents ?? 0) > 0 ? (
          <MoneyRow
            label="Total recibido (sin crédito, con abonos)"
            cents={d.receivedTotalCents ?? d.totalSalesCents}
          />
        ) : null}
      </Section>

      {/* Abonos a cartera */}
      {d.collections && d.collections.length > 0 ? (
        <Section title={`Abonos a cartera (${d.collections.length})`}>
          <table className="w-full text-xs">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-wider text-fg-muted">
              <tr>
                <th className="pb-1 pr-2 font-semibold">Hora</th>
                <th className="pb-1 pr-2 font-semibold">Cliente</th>
                <th className="pb-1 pr-2 font-semibold">Método</th>
                <th className="pb-1 text-right font-semibold">Monto</th>
              </tr>
            </thead>
            <tbody>
              {d.collections.map((c) => (
                <tr key={c.id} className="border-t border-border/50">
                  <td className="py-1 pr-2 tabular-nums">
                    {new Date(c.receivedAt).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="py-1 pr-2">{c.customerName ?? '—'}</td>
                  <td className="py-1 pr-2">
                    {paymentMethodLabels[c.method] ?? c.method}
                  </td>
                  <td className="py-1 text-right font-mono tabular-nums">
                    {formatCOP(c.amountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <MoneyRow
            label="Total abonos"
            cents={d.collectionsTotalCents ?? 0}
            strong
            divider
          />
        </Section>
      ) : null}

      {/* Movimientos */}
      <Section title={`Movimientos del turno (${d.movements.length})`}>
        {d.movements.length === 0 ? (
          <p className="text-xs italic text-fg-muted">
            Sin movimientos registrados.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-wider text-fg-muted">
              <tr>
                <th className="pb-1 pr-2 font-semibold">Hora</th>
                <th className="pb-1 pr-2 font-semibold">Tipo</th>
                <th className="pb-1 pr-2 text-right font-semibold">Monto</th>
                <th className="pb-1 font-semibold">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {d.movements.map((m) => (
                <tr key={m.id} className="border-t border-border/50">
                  <td className="py-1 pr-2 tabular-nums">
                    {new Date(m.at).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="py-1 pr-2">
                    {movementTypeLabels[m.type] ?? m.type}
                  </td>
                  <td className="py-1 pr-2 text-right font-mono tabular-nums">
                    {formatCOP(m.amountCents)}
                  </td>
                  <td className="py-1 italic text-fg-muted">{m.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Pedidos */}
      <Section title={`Pedidos del turno (${d.orders.length})`}>
        {d.orders.length === 0 ? (
          <p className="text-xs italic text-fg-muted">
            Sin ventas registradas.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-wider text-fg-muted">
              <tr>
                <th className="pb-1 pr-2 font-semibold">#</th>
                <th className="pb-1 pr-2 font-semibold">Origen</th>
                <th className="pb-1 pr-2 font-semibold">Estado</th>
                <th className="pb-1 pr-2 text-right font-semibold">Total</th>
                <th className="pb-1 font-semibold">Pago</th>
              </tr>
            </thead>
            <tbody>
              {d.orders.map((o) => {
                const tableLabel =
                  o.origin === 'table'
                    ? (o.tableLabel ??
                      (o.tableNumber != null ? String(o.tableNumber) : null))
                    : null;
                const methods = o.payments
                  .map(
                    (p) =>
                      `${paymentMethodLabels[p.method] ?? p.method} ${formatCOP(p.amountCents)}`,
                  )
                  .join(' + ');
                return (
                  <tr key={o.id} className="border-t border-border/50">
                    <td className="py-1 pr-2 font-mono">
                      {o.id.slice(-6).toUpperCase()}
                    </td>
                    <td className="py-1 pr-2">
                      {orderOriginLabels[o.origin]}
                      {tableLabel ? ` · ${tableLabel}` : ''}
                    </td>
                    <td className="py-1 pr-2">
                      {orderStatusLabels[o.status]}
                    </td>
                    <td className="py-1 pr-2 text-right font-mono tabular-nums">
                      {formatCOP(o.totalCents)}
                    </td>
                    <td className="py-1 text-fg-muted">{methods}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      <div className="mt-8 border-t border-border pt-4 text-center text-[10px] text-fg-muted">
        Atreus POS · {d.session.id}
      </div>

      {/* Toolbar solo en pantalla, no en impresión */}
      <div className="mt-6 flex justify-end gap-2 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-md border border-border bg-fg px-3 py-1.5 text-xs font-semibold text-fg-inverse hover:bg-fg-muted"
        >
          Imprimir
        </button>
        <button
          type="button"
          onClick={() => window.close()}
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-fg hover:bg-surface-muted"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

// ---------- Helpers de layout ----------

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="mb-5 break-inside-avoid">
    <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-fg-muted">
      {title}
    </h2>
    <div>{children}</div>
  </section>
);

const KV = ({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: number;
}) => (
  <div className={span === 2 ? 'col-span-2' : undefined}>
    <span className="font-semibold">{label}: </span>
    <span className="text-fg-muted">{children}</span>
  </div>
);

const MoneyRow = ({
  label,
  cents,
  strong,
  divider,
  negative,
  tone,
}: {
  label: string;
  cents: number;
  strong?: boolean;
  divider?: boolean;
  negative?: boolean;
  tone?: 'ok' | 'bad' | 'warn';
}) => {
  const toneClass =
    tone === 'ok'
      ? 'text-success'
      : tone === 'bad'
        ? 'text-danger'
        : tone === 'warn'
          ? 'text-warning'
          : '';
  return (
    <div
      className={
        'flex items-center justify-between py-1 text-sm ' +
        (divider ? 'mt-1 border-t border-border pt-2 ' : '') +
        (strong ? 'font-bold ' : '') +
        toneClass
      }
    >
      <span>{label}</span>
      <span className="font-mono tabular-nums">
        {negative && cents > 0 ? '-' : ''}
        {formatCOP(Math.abs(cents))}
      </span>
    </div>
  );
};
